<script setup lang="ts">
import { useToast } from '@/composables/useToast';
import { useI18n } from '@/i18n';
import rest from '@/lib/rest';
import { useUserStore } from '@/stores/user';
import { Alert, Button, Divider, Heading, Input, LayoutGap, Prose } from '@dabble/quillui';
import { RestError } from '@dabble/rest-client';
import { computed, onMounted, ref } from 'vue';

const { t, te } = useI18n();
const { showToast } = useToast();
const userStore = useUserStore();

const referralCode = ref('');
const isHydrating = ref(true);
const isGenerating = ref(false);
const noBillingAccount = ref(false);

const hasCode = computed(() => referralCode.value.trim().length > 0);

/** Billing unknown (loading) or confirmed missing — disables referral actions without hiding the row. */
const referralUiLocked = computed(() => isHydrating.value || noBillingAccount.value);

const referralInputPlaceholder = computed(() =>
  noBillingAccount.value && !isHydrating.value ? t('referral_placeholder_no_billing') : t('referral_placeholder'),
);

/** Signup URL with `referralCode` query (see `session-data` sessionParams + `pullSessionFromUrl`). */
function buildReferralSignupLink(): string {
  const code = referralCode.value.trim();
  if (!code) return '';
  const url = new URL(`${window.location.origin}/auth/signup`);
  url.searchParams.set('referralCode', code);
  return url.href;
}

function restErrorMessage(err: unknown): string {
  if (err instanceof RestError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

async function fetchReferralCode() {
  isHydrating.value = true;
  noBillingAccount.value = false;
  try {
    const res = (await rest
      .get('/billing/referral-code')
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include')
      .send()) as { code: string | null };
    referralCode.value = res.code?.trim() ?? '';
  } catch (err: unknown) {
    const message = restErrorMessage(err);
    if (message.includes('NO_BILLING_ACCOUNT')) {
      noBillingAccount.value = true;
    } else {
      showToast('danger', te(message) ? t(message) : message);
    }
  } finally {
    isHydrating.value = false;
  }
}

async function generateReferralCode() {
  if (referralUiLocked.value || hasCode.value) return;
  isGenerating.value = true;
  try {
    const res = (await rest
      .post('/billing/referral-code')
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include')
      .send()) as { code: string };
    referralCode.value = res.code?.trim() ?? '';
    showToast('success', t('referral_toast_generated'));
  } catch (err: unknown) {
    const message = restErrorMessage(err);
    showToast('danger', te(message) ? t(message) : message);
  } finally {
    isGenerating.value = false;
  }
}

onMounted(() => {
  void fetchReferralCode();
});

function copyCodeToClipboard() {
  navigator.clipboard
    .writeText(referralCode.value)
    .then(() => {
      showToast('success', t('referral_toast_copied'));
    })
    .catch(() => {
      showToast('danger', t('referral_toast_copy_failed'));
    });
}

function copyLinkToClipboard() {
  const link = buildReferralSignupLink();
  if (!link) {
    showToast('danger', t('referral_toast_copy_failed'));
    return;
  }
  navigator.clipboard
    .writeText(link)
    .then(() => {
      showToast('success', t('referral_toast_copied'));
    })
    .catch(() => {
      showToast('danger', t('referral_toast_copy_failed'));
    });
}
</script>

<template>
  <LayoutGap direction="column" align="stretch" size="medium" fill class="referral-view">
    <LayoutGap direction="column" align="stretch" size="xsmall" fill>
      <Heading :level="1">{{ t('referral_title') }}</Heading>
      <Divider variant="secondary" size="small" />
    </LayoutGap>

    <LayoutGap direction="column" align="stretch" size="medium" fill class="referral-view__content">
      <Prose variant="secondary">{{ t('referral_description_1') }}</Prose>
      <Prose variant="secondary">{{ t('referral_description_2') }}</Prose>
      <Prose variant="secondary">{{ t('referral_description_3') }}</Prose>

      <Alert v-if="noBillingAccount && !isHydrating" variant="warning" class="referral-view__alert">
        {{ t('referral_error_no_billing') }}
      </Alert>

      <LayoutGap
        direction="row"
        align="stretch"
        vertical-align="center"
        size="tiny"
        fill
        wrap
        class="referral-view__share"
      >
        <Input id="referral-code" v-model="referralCode" type="text" disabled :placeholder="referralInputPlaceholder" />
        <Button
          v-if="!hasCode"
          variant="primary"
          finish="filled"
          :disabled="referralUiLocked || isGenerating"
          @click="generateReferralCode"
        >
          {{ t('referral_generate') }}
        </Button>
        <Button
          icon-left="copy"
          variant="primary"
          finish="filled"
          :disabled="!hasCode || referralUiLocked"
          @click="copyCodeToClipboard"
        >
          {{ t('referral_code') }}
        </Button>
        <Button
          icon-left="copy"
          variant="primary"
          finish="filled"
          :disabled="!hasCode || referralUiLocked"
          @click="copyLinkToClipboard"
        >
          {{ t('referral_link') }}
        </Button>
      </LayoutGap>
    </LayoutGap>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.referral-view {
  max-width: 600px;

  &__content {
    background: theme('color-bg-app-panel');
  }

  &__alert {
    margin-top: space('small');
  }

  &__share {
    padding-top: space('small');

    :deep(.layout-gap) {
      min-width: 0;
    }

    :deep(.input) {
      flex: 1 1 12rem;
      min-width: 8rem;
    }

    :deep(input:disabled) {
      color: theme('color-text-primary');
      opacity: 1;
      -webkit-text-fill-color: theme('color-text-primary');
    }

    :deep(input:disabled::placeholder) {
      color: theme('color-text-placeholder');
      -webkit-text-fill-color: theme('color-text-placeholder');
    }
  }

  &__actions {
    :deep(.button) {
      min-width: 140px;
    }
  }
}
</style>
